import { useCallback, useEffect, useState } from "react"
import type { CogoElement } from "../core/types"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog"
import { ElementRenderer } from "./element-renderer"

type CanonicalModalElement = CogoElement & {
  closable?: boolean | string
  defaultOpen?: boolean | string
}

const MODAL_SIZE_CLASSES = {
  sm: "max-w-sm",
  default: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  full: "h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)]",
} as const

type ModalSize = keyof typeof MODAL_SIZE_CLASSES

function resolveBoolean(
  value: boolean | string | undefined,
  resolveSymbol: (value: string) => unknown,
  fallback: boolean,
): boolean {
  if (value === undefined) return fallback
  if (typeof value === "boolean") return value

  const resolved = resolveSymbol(value)
  if (typeof resolved === "boolean") return resolved
  return String(resolved ?? value).trim().toLowerCase() === "true"
}

function resolveSize(size: string | undefined): ModalSize {
  return size && size in MODAL_SIZE_CLASSES
    ? size as ModalSize
    : "default"
}

/**
 * Canonical `modal` adapter.
 *
 * `modal` is an ontology alias of dialog, but its authored contract is flat:
 * title/open/visible/closable/size live on the modal element itself rather
 * than in dialog-content/dialog-trigger slots.
 */
export function ModalRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const modal = element as CanonicalModalElement
  const visibleSource = modal.visible
  const resolvedVisible = typeof visibleSource === "string"
    ? resolveSymbol(visibleSource)
    : visibleSource
  const requestedOpen = resolveBoolean(
    modal.open ?? modal.visible ?? modal.defaultOpen,
    resolveSymbol,
    false,
  )
  const closable = resolveBoolean(modal.closable, resolveSymbol, true)
  const size = resolveSize(modal.size)
  const titleSource = modal.title ?? modal.text
  const title = titleSource
    ? String(resolveSymbol(titleSource) ?? titleSource)
    : undefined
  const [open, setOpen] = useState(requestedOpen)

  useEffect(() => {
    setOpen(requestedOpen)
  }, [requestedOpen])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && !closable) return

      setOpen(nextOpen)
      if (!nextOpen) {
        dispatchEvent(element.id, "close", {})
      }
    },
    [closable, dispatchEvent, element.id],
  )

  return (
    <span
      data-atom-type="modal"
      data-size={size}
      data-closable={closable ? "true" : "false"}
      data-is-open={open ? "true" : "false"}
      data-visible={resolvedVisible == null ? visibleSource : String(resolvedVisible)}
      data-title={title}
      data-testid={`modal-${element.id}`}
      aria-expanded={open}
      aria-label={title}
      className={element.className ? cn("inline-block", element.className) : undefined}
    >
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        disablePointerDismissal={!closable}
      >
        <DialogContent
          showCloseButton={closable}
          className={cn(
            MODAL_SIZE_CLASSES[size],
            element.className,
          )}
          data-testid={`modal-content-${element.id}`}
        >
          <DialogTitle className={title ? undefined : "sr-only"}>
            {title ?? "Modal"}
          </DialogTitle>
          {element.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </DialogContent>
      </Dialog>
    </span>
  )
}
